import { AsyncPipe } from '@angular/common';
import { Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { Router } from '@angular/router';
import { EMPTY, catchError, filter, take, tap } from 'rxjs';
import { AppIconComponent } from 'application/src/app/components/common/app-icon/app-icon.component';
import { PeechUserService } from 'application/src/app/services/peech-user.service';
import { TableOfContentsEntry } from 'application/src/app/services/library.service';
import {
  RESUBSCRIBE_ROUTE,
  resubscribeQueryParams,
} from 'application/src/app/utils/resubscribe.util';
import { ModalComponent } from 'shared/src/components/modal/modal.component';
import { AnalyticsService } from 'shared/src/services/analytics.service';
import {
  PlayerService,
  PlayerSnapshot,
  SKIP_BACK_SECONDS,
  SKIP_FORWARD_SECONDS,
} from '../player.service';
import { StreamErrorCode } from '../stream-error';
import { VoicePreset, VoicePresetsService } from '../voice-presets.service';

interface PanelTocRow {
  label: string;
  partIndex: number | null;
  depth: number;
}

function flattenToc(entries: TableOfContentsEntry[], depth = 0): PanelTocRow[] {
  return entries.flatMap(entry => [
    { label: entry.label, partIndex: entry.partIndex, depth },
    ...flattenToc(entry.children, depth + 1),
  ]);
}

const SPEED_OPTIONS = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2, 2.5, 3];
const SLEEP_OPTIONS_MINUTES = [5, 10, 15, 30, 45, 60];
const VOICE_PRESET_AVATAR_BASE =
  'https://storage.googleapis.com/peech-public-resources/voice-presets-avatars';

@Component({
  selector: 'app-audio-panel',
  templateUrl: './audio-panel.component.html',
  styleUrls: ['./audio-panel.component.scss'],
  imports: [AsyncPipe, MatMenuModule, AppIconComponent],
})
export class AudioPanelComponent {
  readonly player = inject(PlayerService);
  private readonly voicePresets = inject(VoicePresetsService);
  private readonly peechUser = inject(PeechUserService);
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);
  private readonly analytics = inject(AnalyticsService);
  private readonly destroyRef = inject(DestroyRef);

  readonly snapshot$ = this.player.snapshot$;
  readonly speedOptions = SPEED_OPTIONS;
  readonly sleepOptions = SLEEP_OPTIONS_MINUTES;
  readonly skipBackSeconds = SKIP_BACK_SECONDS;
  readonly skipForwardSeconds = SKIP_FORWARD_SECONDS;

  presets: VoicePreset[] | null = null;
  presetsLoading = false;
  private presetsItemId: string | null = null;

  // Opens the discount modal once per SUBSCRIPTION_REQUIRED occurrence. Re-arming waits
  // for the snapshot to leave the error AND the dialog to be gone: an external retry
  // (headset/lock-screen play) flips the snapshot through 'loading' and back to the error
  // while the dialog is still up, and that must not stack a second one.
  private subscriptionModalArmed = true;
  private subscriptionDialogRef: MatDialogRef<ModalComponent, boolean> | null = null;

  // The offer belongs to whoever hit the error: the id is captured when the modal opens,
  // both CTAs navigate with it, and the dialog closes if the account changes under it —
  // an offer raised for account A must never identify account B on the paywall.
  private offerUserId: string | null = null;
  private currentUserId: string | null = null;
  private isSubscriptionErrorNow = false;

  constructor() {
    this.peechUser.details$
      .pipe(
        tap(details => {
          this.currentUserId = details?.userId ?? null;

          if (this.subscriptionDialogRef && this.offerUserId !== this.currentUserId) {
            this.subscriptionDialogRef.close();
          }
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();

    this.snapshot$
      .pipe(
        tap(snapshot => {
          this.isSubscriptionErrorNow =
            snapshot.status === 'error' && snapshot.error === 'SUBSCRIPTION_REQUIRED';

          if (!this.isSubscriptionErrorNow) {
            if (!this.subscriptionDialogRef) {
              this.subscriptionModalArmed = true;
            }
            return;
          }

          if (this.subscriptionModalArmed) {
            this.subscriptionModalArmed = false;
            this.openSubscriptionModal();
          }
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  // Tracks the one presetId whose avatar 404'd, so the broken <img> isn't re-requested
  // on every change-detection tick. Comparing against the *current* presetId (rather than
  // a plain boolean) is what makes this reset itself once the preset changes.
  private failedAvatarPresetId: string | null = null;

  // While the user holds the seek thumb, snapshot updates must not yank it back — the
  // live value wins until the release commits the seek.
  seekDragging = false;

  togglePlay(snapshot: PlayerSnapshot): void {
    if (snapshot.status === 'playing' || snapshot.status === 'loading') {
      this.player.pause();
    } else if (snapshot.status === 'paused') {
      this.player.resume();
    } else if (snapshot.status === 'ended') {
      // Through the service's replay API, not a bare playPart(0) — a replay starts a
      // fresh autoplay chain.
      this.player.replayEndedItem();
    } else if (snapshot.status === 'error') {
      this.player.retryPart();
    }
  }

  seekStart(): void {
    this.seekDragging = true;
  }

  seekCommit(value: string): void {
    this.seekDragging = false;
    this.player.seek(Number(value));
  }

  // The seek must be committed here, on pointerup, not left to 'change': pointerup's
  // change-detection pass rewrites [value] back to snapshot.currentTime the moment the
  // dragging flag drops, which erases the tapped position before 'change' can read it —
  // a plain tap on the track would seek nowhere. 'change' stays only for keyboard seeks.
  seekRelease(value: string): void {
    if (this.seekDragging) {
      this.player.seek(Number(value));
    }

    this.seekDragging = false;
  }

  // A cancelled gesture (scroll takeover, pointer capture loss) abandons the drag
  // without committing — the thumb snaps back to the playing position.
  seekCancel(): void {
    this.seekDragging = false;
  }

  playedPercent(snapshot: PlayerSnapshot, liveValue: string): number {
    if (!snapshot.duration) {
      return 0;
    }

    const current = this.seekDragging ? Number(liveValue) : snapshot.currentTime;

    return Math.min(100, (current / snapshot.duration) * 100);
  }

  seekValue(snapshot: PlayerSnapshot, liveValue: string): number {
    return this.seekDragging ? Number(liveValue) : snapshot.currentTime;
  }

  formatTime(seconds: number | null): string {
    if (seconds === null || !Number.isFinite(seconds)) {
      return '–:––';
    }

    const whole = Math.max(0, Math.floor(seconds));
    const minutes = Math.floor(whole / 60);
    const secs = whole % 60;

    return `${minutes}:${String(secs).padStart(2, '0')}`;
  }

  formatSpeed(speed: number): string {
    return `${speed}×`;
  }

  // MM:SS countdown while armed, shown in place of the moon icon.
  formatSleep(seconds: number): string {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;

    return `${minutes}:${String(secs).padStart(2, '0')}`;
  }

  openText(snapshot: PlayerSnapshot): void {
    if (snapshot.item) {
      this.router.navigate(['/item', snapshot.item.id]);
    }
  }

  errorLabel(code: StreamErrorCode | null): string {
    switch (code) {
      case 'CREDITS_EXHAUSTED':
        return 'You are out of credits';
      case 'SUBSCRIPTION_REQUIRED':
        return 'Subscription required to keep listening';
      case 'LANGUAGE_NOT_SUPPORTED':
        return 'This language is not supported yet';
      case 'CONTENT_UNVOICEABLE':
        return 'This item has no voiceable text';
      case 'NETWORK':
        return 'Connection problem — check your network';
      case 'TRUNCATED':
        return 'Playback stopped early — tap play to retry';
      default:
        return 'Could not play this part';
    }
  }

  partNumbers(snapshot: PlayerSnapshot): number[] {
    return Array.from({ length: snapshot.partsCount }, (_, index) => index);
  }

  // A non-empty TOC supersedes the parts menu, mirroring iOS (IOS-1982); with neither
  // TOC nor multiple parts the entry point disappears entirely (IOS-1920).
  hasToc(snapshot: PlayerSnapshot): boolean {
    return (snapshot.item?.tableOfContents.length ?? 0) > 0;
  }

  // Flattened per TOC array, cached by reference — the menu template re-reads it on
  // every change-detection pass while open, and every (re)start builds a fresh
  // PlayingItem, so a backfilled TOC on the same item invalidates naturally.
  tocRows(snapshot: PlayerSnapshot): PanelTocRow[] {
    const item = snapshot.item;
    if (!item) {
      return [];
    }
    if (this.tocRowsSource !== item.tableOfContents) {
      this.tocRowsSource = item.tableOfContents;
      this.cachedTocRows = flattenToc(item.tableOfContents);
    }
    return this.cachedTocRows;
  }

  // The chapter being listened to: the last navigable entry at or before the current
  // part (entries with a null partIndex are not navigable, BAC-523). Memoized per
  // (rows, partIndex) — the open menu evaluates this from every row's class bindings on
  // each of playback's ~2/s snapshots, and a rescan per call is O(N²) on big books.
  activeTocRowIndex(snapshot: PlayerSnapshot): number {
    const rows = this.tocRows(snapshot);
    if (this.activeTocRowsSource === rows && this.activeTocPartIndex === snapshot.partIndex) {
      return this.cachedActiveTocIndex;
    }

    let active = -1;
    for (let i = 0; i < rows.length; i++) {
      const partIndex = rows[i].partIndex;
      if (partIndex !== null && partIndex <= snapshot.partIndex) {
        active = i;
      }
    }

    this.activeTocRowsSource = rows;
    this.activeTocPartIndex = snapshot.partIndex;
    this.cachedActiveTocIndex = active;
    return active;
  }

  playTocRow(row: PanelTocRow): void {
    if (row.partIndex !== null) {
      this.player.playPart(row.partIndex, 'parts_menu');
    }
  }

  private tocRowsSource: TableOfContentsEntry[] | null = null;
  private cachedTocRows: PanelTocRow[] = [];
  private activeTocRowsSource: PanelTocRow[] | null = null;
  private activeTocPartIndex: number | null = null;
  private cachedActiveTocIndex = -1;

  loadPresets(): void {
    const itemId = this.player.snapshot.item?.id;

    if (!itemId || (this.presetsItemId === itemId && this.presets)) {
      return;
    }

    this.presetsLoading = true;
    this.presetsItemId = itemId;
    this.voicePresets
      .getPresets(itemId)
      .pipe(
        tap(response => {
          // A slow response for a previous item must not overwrite the current one's
          // voices — selecting from the stale list would apply the wrong preset.
          if (this.presetsItemId !== itemId) {
            return;
          }

          this.presets = response.presets;
          this.presetsLoading = false;
        }),
        catchError(() => {
          if (this.presetsItemId === itemId) {
            this.presets = null;
            this.presetsItemId = null;
            this.presetsLoading = false;
          }

          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  isSelectedPreset(preset: VoicePreset): boolean {
    return preset.id === this.player.snapshot.presetId;
  }

  voiceAvatarUrl(presetId: string | null): string | null {
    if (!presetId || this.failedAvatarPresetId === presetId) {
      return null;
    }

    return `${VOICE_PRESET_AVATAR_BASE}/${presetId.toLowerCase()}/avatar.jpg`;
  }

  onVoiceAvatarError(presetId: string | null): void {
    this.failedAvatarPresetId = presetId;
  }

  openTopUp(): void {
    this.peechUser.details$
      .pipe(
        filter(details => details !== null),
        take(1),
        tap(details =>
          this.router.navigate(['/credits'], {
            queryParams: { userId: details.userId, redirect: this.router.url },
          })
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  // Error-row CTA, parallel to openTopUp() above.
  openSubscriptionOffer(): void {
    this.goToDiscountOffer('error_row');
  }

  private openSubscriptionModal(): void {
    if (this.subscriptionDialogRef) {
      return;
    }

    this.offerUserId = this.currentUserId;
    this.analytics.track('subscription_required_modal_shown', { source: 'stream' });

    this.subscriptionDialogRef = this.dialog.open(ModalComponent, {
      data: {
        title: 'Keep listening with Premium',
        message:
          'This item needs an active subscription to keep playing. Good news — we saved a special discount for you, so you can subscribe for less and pick up right where you left off.',
        okButtonTitle: 'Get my discount',
        cancelButtonTitle: 'Not now',
        isConfirmation: true,
      },
    });

    // No takeUntilDestroyed: the dialog outlives the panel, and its close must still
    // navigate or track the dismissal.
    this.subscriptionDialogRef
      .afterClosed()
      .pipe(
        tap(isOk => {
          this.subscriptionDialogRef = null;

          // The snapshot may have left the error while the dialog was open (an external retry
          // succeeded) — that emission couldn't re-arm past the open dialog, so re-arm here or
          // the next occurrence would never auto-open.
          if (!this.isSubscriptionErrorNow) {
            this.subscriptionModalArmed = true;
          }

          if (isOk) {
            this.goToDiscountOffer('stream');
          } else {
            this.analytics.track('subscription_required_modal_dismissed', { source: 'stream' });
          }
        })
      )
      .subscribe();
  }

  // TODO: web has no dedicated paywall/placement for this case yet; the discount paywall
  // is a stopgap. iOS opens the "limit reached" paywall (Adapty placement 'triggering2')
  // here — a real web placement + paywall should replace this navigation.
  private goToDiscountOffer(source: 'stream' | 'error_row'): void {
    // Navigate only with the id captured when the error was raised, and only while that
    // account is still the signed-in one — a sign-out or A→B switch since then drops the
    // click instead of borrowing the new identity for the old account's offer.
    const userId = this.offerUserId;

    if (!userId || userId !== this.currentUserId) {
      return;
    }

    this.analytics.track('subscription_required_modal_cta_tapped', { source });

    this.router.navigate([RESUBSCRIBE_ROUTE], {
      queryParams: resubscribeQueryParams({
        userId,
        trigger: 'streamSubscriptionRequired',
        utmMedium: 'stream_subscription_modal',
        redirect: this.router.url,
      }),
    });
  }
}
